import { combineLatest, filter, type Observable, type OperatorFunction, scan, startWith, take } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import type { FilterConfig } from './filter-config';
import type { FilterDataResult } from './filter-data-result';
import { FilterConfigService, FilterConfigToken } from './filter-config.service';
import { isDataResult, isErrorResult } from '@team23/ts-result';

/**
 * An abstraction of the filter logic providing options, placeholder and reload functionality.
 * may need to optimized further so better support search and other filter types (e.g. multiselect)
 */
@Injectable({ providedIn: 'root' })
export class FilterService {

    protected _filters = inject(FilterConfigToken);

    public filters$: Observable<Array<FilterConfig>> = combineLatest(this._filters.map(config => this.createFilter$(config)))
        .pipe(
            startWith([]),
        );

    public reloadErrored(): void {
        this._filters.forEach(config => this._reloadErrored(config));
    }

    protected _reloadErrored(config: FilterConfigService): void {
        config.options$.pipe(this._onError()).subscribe(() => {
            config.reload$.next();
        });
    }

    protected _onError(): OperatorFunction<FilterDataResult, FilterDataResult> {
        return (subject: Observable<FilterDataResult>) => subject.pipe(
            take(1),
            filter(value => !!value.error),
        );
    }

    public createFilter$(config: FilterConfigService): Observable<FilterConfig> {
        return config.options$.pipe(
            scan((state: FilterConfig, options: FilterDataResult): FilterConfig => {
                if (isDataResult(options)) {
                    return {
                        type: state.type,
                        filter: state.filter,
                        placeholder: this._getPlaceholder(options, state.placeholder),
                        options: { data: options.data },
                    };
                }

                if (isErrorResult(options)) {
                    return {
                        type: state.type,
                        filter: state.filter,
                        placeholder: state.placeholder,
                        options: { error: options.error },
                    };
                }

                return {
                    type: state.type,
                    filter: state.filter,
                    placeholder: state.placeholder,
                    options: { data: [] },
                };
            }, {
                type: config.type,
                filter: config.filterModel,
                options: { data: [] },
                placeholder: config.placeholder,
            }),
        );
    }

    protected _getPlaceholder(options: FilterDataResult, fallback: string): string {
        return options.data?.length
            ? `z.B. ${ options.data[0]?.label }`
            : fallback;
    }
}
